import React from "react";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { GetAllFilesAndFoldersByUserDto } from "../../../types/FolderPermission";
import { FileActions, FolderFileType } from "../../../data/Enums";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import UnPublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import { ACTIONITEMVALIDATIONS} from "../../../constants/messages";
import './ActionsMenu.scss';
import { ModuleNames, permissionMap } from "../../../data/constants";
import { getModulePermission } from "../../../utils/commonHelper";
import { useMenu } from "../../../context/MenuContext";

interface ActionsMenuProps {
  currentRow: GetAllFilesAndFoldersByUserDto;
  menuAnchorEl: HTMLElement | null;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, row: GetAllFilesAndFoldersByUserDto) => void;
  handleMenuClose: () => void;
  handleAction: (event: React.MouseEvent, action: FileActions) => void;
  displayRightSide: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  currentRow,
  menuAnchorEl,
  handleMenuOpen,
  handleMenuClose,
  handleAction,
  displayRightSide
}) => {

  const { permissions } = useMenu();

  // eslint-disable-next-line no-undef
  const actionIcons: Record<string, JSX.Element> = {
    Download: <FileDownloadOutlinedIcon />,
    AddNew: <AddCircleOutlineOutlinedIcon />,
    Edit: <BorderColorOutlinedIcon />,
    Publish: <PublishOutlinedIcon />,
    UndoPublish: <UnPublishedOutlinedIcon />,
    ShareToRole: <ShareOutlinedIcon />,
    ShareToUser: <ShareOutlinedIcon />,
    MakeACopy: <ContentCopyOutlinedIcon />,
    MoveToBin: <DeleteOutlineOutlinedIcon />,
  };

  const filteredActions = Object.keys(FileActions)
    .filter((actionKey) => {
      const key = FileActions[actionKey as keyof typeof FileActions];
      const permission = permissionMap[actionKey as keyof typeof FileActions];
      const action = actionKey as keyof typeof FileActions;

      const mainModulePermission = getModulePermission(action, ModuleNames.FileExplorer, permissions);

      if (key === FileActions.Publish) {
        return mainModulePermission && currentRow.isPublish === false;
      }
      else if (key === FileActions.UndoPublish) {
        return mainModulePermission && currentRow.isPublish === true && currentRow[permissionMap[FileActions.Edit]];
      }
      else if (
        key.replace(/\s+/g, "") === FileActions.AddNew.replace(/\s+/g, "") &&
        currentRow.type === FolderFileType[FolderFileType.File]
      ) {
        return false;
      } else {
        return mainModulePermission && currentRow[permission] === true;
      }
    })
    .map((key) => {
      return (
        <MenuItem
          key={key}
onClick={(e) => {
  e.stopPropagation();
  e.preventDefault();

  // Ensure the row is set before executing the action
  handleMenuOpen(e, currentRow);

  handleAction(e, FileActions[key as keyof typeof FileActions]);
}}
        >

          <ListItemIcon>
            {actionIcons[key] || <BorderColorOutlinedIcon />} {/* Fallback icon */}
          </ListItemIcon>
            <ListItemText>{FileActions[key as keyof typeof FileActions]}</ListItemText>
        </MenuItem>
      )
    });
  return (
    <>
      <div className={displayRightSide ? "menu-icon right-align" : "menu-icon"}>
        <Tooltip title="More actions" placement="top">
          <IconButton size="small" onClick={(event) => handleMenuOpen(event, currentRow)} aria-controls="actions-menu" aria-haspopup="true">
            <MoreVertIcon style={{ cursor: "pointer" }} fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <Menu
        id="actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {filteredActions.length > 0 ? filteredActions : <MenuItem key="no-permissions" disabled>{ACTIONITEMVALIDATIONS.READONLY_ACCESS}</MenuItem>}
      </Menu>

    </>
  );
};

export default React.memo(ActionsMenu);
